/**
 * ControlGen client service.
 *
 * This is deliberately the ONLY place the UI learns where its data comes from.
 * Components import the generated hooks and never see a URL, which is what lets
 * the backing store change without touching a single screen.
 *
 * That matters more than usual here. Today `/api/controlgen/*` proxies to the
 * authoring engine. The decision on record is that the engine goes stateless
 * and fairly-app owns persistence, so these same endpoints will later be served
 * from our own Mongo. When that happens this file's `baseUrl` and the server
 * controller change; the screens do not.
 *
 * Keep the response types (`~/shared/types/controlgen-types`) stable across that
 * move. They describe the contract, not the storage.
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from './axios-base-query';

import type { ExportTarget } from '~/shared/controlgen/export-targets';
import type {
  FrameworksPage,
  FrameworksQuery,
} from '~/shared/controlgen/regulation-merge';
import type {
  IngestAccepted,
  SpineEdit,
  RunProgress,
  IngestFromFileRequest,
  IngestFromUrlRequest,
  StartRunAccepted,
  StartRunRequest,
  DocRecordList,
  EngineHealth,
  FrameworkUpdate,
  FrameworkUpdateList,
  RegulatoryDocumentDetail,
  RegulatoryDocumentList,
  RunDetail,
  RunGenerateList,
  RunList,
  RunScope,
  ControlVerification,
  ControlReview,
  ControlDraft,
  PutDraftResult,
  SaveControlResult,
  ControlEditFields,
  RunAnchors,
  DocumentText,
  DocumentOriginalInfo,
  AssessmentPack,
  EvaluationPack,
  EvaluationSeedCounts,
  ExportFidelity,
  MonitoringPolicyAttachments,
  RuntimePack,
  MonitoringPolicyLoadResult,
  RedteamRunResult,
  RunSpine,
  UpdateDiff,
  UpdateRegions,
  AuthoredControl,
  ControlRecord,
  BundleVariant,
} from '~/shared/types/controlgen-types';
import type {
  RegulatorySourceInput,
  RegulatorySourceRecord,
  ScanResult,
  ScanRunRecord,
  SchedulerStateRecord,
} from '~/shared/types/regulatory-source-types';
import type {
  FindingPageQuery,
  RejectionReason,
  SourceFindingInput,
  SourceFindingPage,
  SourceFindingRecord,
} from '~/shared/types/source-finding-types';
import type {
  TrackedRegulationInput,
  TrackedRegulationRecord,
} from '~/shared/types/tracked-regulation-types';
import type { CoverageSummary } from '~/shared/utils/policy-coverage-model';

/**
 * Paging arguments for the two engine collections that page.
 *
 * `/documents` and `/updates` read `limit` and `offset`; the engine defaults to
 * 100 and will not return more than 500. Every other ControlGen collection
 * returns everything it holds and ignores these, so the arguments are typed only
 * where they work. An argument that does nothing is worse than no argument: the
 * call site then reads as paged when it is not.
 */
export interface PageArgs {
  limit?: number;
  offset?: number;
}

/**
 * What a caller asks for when it needs the WHOLE collection.
 *
 * The Frameworks table joins tracked regulations, documents and updates by name,
 * so a document that never arrived silently drops its row — and any detected
 * change that points at it. 500 is the engine's maximum rather than a guess.
 *
 * ⚠ Asking for the maximum is not the same as being told you received
 * everything. Callers compare `returned` against `count` and say so on screen;
 * a cap that is reported is a caveat, and a cap that is assumed is a wrong list.
 */
export const WHOLE_COLLECTION: PageArgs = { limit: 500 };

function pageSuffix(args: PageArgs | void): string {
  if (!args) {
    return '';
  }
  const parts: string[] = [];
  if (typeof args.limit === 'number') {
    parts.push(`limit=${args.limit}`);
  }
  if (typeof args.offset === 'number') {
    parts.push(`offset=${args.offset}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export const controlGenApi = createApi({
  reducerPath: 'controlGenApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/api/controlgen' }),
  tagTypes: [
    // Everything that describes one control's current state: the control
    // itself, its version record, its chain verification and its draft. A
    // promote changes all four at once, so they share a tag — without one,
    // publishing left the version pane serving a cached "Version 1" beside a
    // message saying version 2 had just been created.
    'Control',
    'Document',
    'Update',
    'TrackedRegulation',
    'Source',
    'Finding',
    'Run',
    // Where a loaded policy is attached, for the run page's "Use this bundle"
    // cards. Untagged, it served "Already attached" after the policy had been
    // removed, until a reload.
    'PolicyAttachment',
    'RunArchive',
  ],
  endpoints: (builder) => ({
    /** The engine naming the store it serves — see EngineHealth. */
    getEngineHealth: builder.query<EngineHealth, void>({
      query: () => ({ url: '/health', method: 'get' }),
    }),

    // ── Regulatory sources ────────────────────────────────────────────────
    getRegulatoryDocuments: builder.query<
      RegulatoryDocumentList,
      PageArgs | void
    >({
      query: (args) => ({
        url: `/documents${pageSuffix(args)}`,
        method: 'get',
      }),
      providesTags: ['Document'],
    }),

    /**
     * The Frameworks tab's single source — tracked regulations, documents and
     * updates merged, filtered, sorted and paged on the server, so only one
     * page crosses the wire. Tagged with all three sources so an add or an
     * ingest re-fetches the page rather than leaving a stale total.
     */
    getFrameworksPage: builder.query<FrameworksPage, FrameworksQuery>({
      query: ({ filter, sort, direction, search, limit, offset }) => {
        const params = new URLSearchParams();
        params.set('filter', filter);
        params.set('sort', sort);
        if (direction) {
          params.set('dir', direction);
        }
        if (search.trim()) {
          params.set('search', search.trim());
        }
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        return { url: `/frameworks?${params.toString()}`, method: 'get' };
      },
      providesTags: ['Document', 'Update', 'TrackedRegulation'],
    }),

    // NOT `RegulatoryDocument` — the detail endpoint returns a different shape
    // from the list item, in both directions. See the type's own note.
    getRegulatoryDocument: builder.query<RegulatoryDocumentDetail, string>({
      query: (documentId) => ({
        url: `/documents/${encodeURIComponent(documentId)}`,
        method: 'get',
      }),
      providesTags: ['Document'],
    }),

    /**
     * Tracked regulations — served from OUR Mongo, not proxied to the engine.
     * The front of the pipeline: sighted, not yet ingested. 124 of these
     * against 49 ingested documents, with roughly one in common.
     */
    getTrackedRegulations: builder.query<
      { count: number; regulations: TrackedRegulationRecord[] },
      void
    >({
      query: () => ({ url: '/tracked-regulations', method: 'get' }),
      providesTags: ['TrackedRegulation'],
    }),

    getTrackedRegulation: builder.query<TrackedRegulationRecord, string>({
      query: (id) => ({
        url: `/tracked-regulations/${encodeURIComponent(id)}`,
        method: 'get',
      }),
      providesTags: ['TrackedRegulation'],
    }),

    /**
     * The only writes in ControlGen — everything else here is read-only, since
     * the engine is reached through an allow-list proxy that forwards GETs
     * alone. Both invalidate the tag so the merged list re-counts itself rather
     * than showing a stale total after an edit.
     */
    createTrackedRegulation: builder.mutation<
      TrackedRegulationRecord,
      TrackedRegulationInput
    >({
      query: (body) => ({
        url: '/tracked-regulations',
        method: 'post',
        data: body,
      }),
      invalidatesTags: ['TrackedRegulation'],
    }),

    updateTrackedRegulation: builder.mutation<
      TrackedRegulationRecord,
      { id: string; body: TrackedRegulationInput }
    >({
      query: ({ id, body }) => ({
        url: `/tracked-regulations/${encodeURIComponent(id)}`,
        method: 'put',
        data: body,
      }),
      invalidatesTags: ['TrackedRegulation'],
    }),

    // ── Regulatory sources (the places we watch) ──────────────────────────
    getRegulatorySources: builder.query<
      { count: number; sources: RegulatorySourceRecord[] },
      void
    >({
      query: () => ({ url: '/sources', method: 'get' }),
      providesTags: ['Source'],
    }),

    getRegulatorySource: builder.query<RegulatorySourceRecord, string>({
      query: (id) => ({
        url: `/sources/${encodeURIComponent(id)}`,
        method: 'get',
      }),
      providesTags: ['Source'],
    }),

    createRegulatorySource: builder.mutation<
      RegulatorySourceRecord,
      RegulatorySourceInput
    >({
      query: (body) => ({ url: '/sources', method: 'post', data: body }),
      invalidatesTags: ['Source'],
    }),

    updateRegulatorySource: builder.mutation<
      RegulatorySourceRecord,
      { id: string; body: RegulatorySourceInput }
    >({
      query: ({ id, body }) => ({
        url: `/sources/${encodeURIComponent(id)}`,
        method: 'put',
        data: body,
      }),
      invalidatesTags: ['Source'],
    }),

    /**
     * Records that a PERSON checked this source. Distinct from scanning: it is
     * what you do for a web page or an API, which nothing can read for you.
     */
    markRegulatorySourceChecked: builder.mutation<
      RegulatorySourceRecord,
      string
    >({
      query: (id) => ({
        url: `/sources/${encodeURIComponent(id)}/checked`,
        method: 'put',
      }),
      invalidatesTags: ['Source'],
    }),

    /**
     * Really fetches the feed. RSS only; other kinds are refused by the scanner
     * rather than silently reported as a clean pass. Invalidates findings too,
     * since a successful scan is how the review queue fills.
     */
    scanRegulatorySource: builder.mutation<
      { source: RegulatorySourceRecord; result: ScanResult },
      string
    >({
      query: (id) => ({
        url: `/sources/${encodeURIComponent(id)}/scanned`,
        method: 'put',
      }),
      invalidatesTags: ['Source', 'Finding'],
    }),

    /** The scheduler's heartbeat. `run: null` means it has never run. */
    getLatestScanRun: builder.query<
      {
        run: ScanRunRecord | null;
        scheduler: SchedulerStateRecord | null;
      },
      void
    >({
      query: () => ({ url: '/scan-runs/latest', method: 'get' }),
      providesTags: ['Source'],
    }),

    // ── Review queue (what sources turn up) ───────────────────────────────
    getSourceFindings: builder.query<SourceFindingPage, FindingPageQuery>({
      query: ({ state, q, page, limit }) => {
        const params = new URLSearchParams();
        params.set('state', state);
        if (q) {
          params.set('q', q);
        }
        params.set('page', String(page));
        params.set('limit', String(limit));
        return { url: `/findings?${params.toString()}`, method: 'get' };
      },
      providesTags: ['Finding'],
    }),

    getSourceFinding: builder.query<SourceFindingRecord, string>({
      query: (id) => ({
        url: `/findings/${encodeURIComponent(id)}`,
        method: 'get',
      }),
      providesTags: ['Finding'],
    }),

    createSourceFinding: builder.mutation<
      SourceFindingRecord,
      SourceFindingInput
    >({
      query: (body) => ({ url: '/findings', method: 'post', data: body }),
      invalidatesTags: ['Finding'],
    }),

    rejectSourceFinding: builder.mutation<
      SourceFindingRecord,
      { id: string; reason: RejectionReason; note: string | null }
    >({
      query: ({ id, reason, note }) => ({
        url: `/findings/${encodeURIComponent(id)}/rejected`,
        method: 'put',
        data: { reason, note },
      }),
      invalidatesTags: ['Finding'],
    }),

    /** Only after the regulation exists — see the controller's note. */
    acceptSourceFinding: builder.mutation<
      SourceFindingRecord,
      { id: string; trackedRegulationId: string }
    >({
      query: ({ id, trackedRegulationId }) => ({
        url: `/findings/${encodeURIComponent(id)}/accepted`,
        method: 'put',
        data: { trackedRegulationId },
      }),
      invalidatesTags: ['Finding', 'TrackedRegulation'],
    }),

    /**
     * Polled while a run is live. Its own query rather than part of `getRun` so
     * polling costs one small read instead of re-fetching the whole run detail
     * every few seconds.
     */
    getRunProgress: builder.query<RunProgress, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/progress`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    /**
     * Spine review. Applying edits spends nothing; RESUMING pays for whatever it
     * regenerates, which is why the two are separate calls with separate gates
     * rather than one "save and continue".
     */
    applySpineEdits: builder.mutation<
      unknown,
      { runId: string; ops: SpineEdit[] }
    >({
      query: ({ runId, ops }) => ({
        url: `/runs/${encodeURIComponent(runId)}/spine/edits`,
        method: 'post',
        data: { ops },
      }),
      invalidatesTags: ['Control', 'Run'],
    }),

    resumeRun: builder.mutation<unknown, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/spine/resume`,
        method: 'post',
        data: { confirmation: { runId, acknowledgedSpend: true } },
      }),
      invalidatesTags: ['Run'],
    }),

    /**
     * Stage 3b — loading a regulation's text so it can be generated from.
     *
     * Invalidates `RegulatoryDocument` so the Regulations list moves the row to
     * "Ready to generate" without a manual refresh.
     */
    ingestDocument: builder.mutation<IngestAccepted, IngestFromFileRequest>({
      query: (body) => ({ url: '/documents', method: 'post', data: body }),
      invalidatesTags: ['Document'],
    }),

    ingestDocumentFromUrl: builder.mutation<
      IngestAccepted,
      IngestFromUrlRequest
    >({
      query: (body) => ({
        url: '/documents/from-url',
        method: 'post',
        data: body,
      }),
      invalidatesTags: ['Document'],
    }),

    /**
     * Stage 3c — the ONLY mutation in this file that spends money.
     *
     * Everything that decides whether it is allowed lives on the server: org
     * capability, the `CONTROLGEN_ALLOW_RUN_EXECUTION` flag, and a confirmation
     * that must echo the document id. None of those can be satisfied from here,
     * which is the point — a client-side guard would be advice, not a gate.
     *
     * Invalidates `Run` so the list and the document's generation history both
     * show the new run without a manual refresh.
     */
    startRun: builder.mutation<StartRunAccepted, StartRunRequest>({
      query: (body) => ({
        url: '/runs',
        method: 'post',
        data: body,
      }),
      invalidatesTags: ['Run'],
    }),

    reopenSourceFinding: builder.mutation<SourceFindingRecord, string>({
      query: (id) => ({
        url: `/findings/${encodeURIComponent(id)}/reopened`,
        method: 'put',
      }),
      invalidatesTags: ['Finding'],
    }),

    /** Version chains — only 16 of 49 documents belong to one. */
    getDocRecords: builder.query<DocRecordList, void>({
      query: () => ({ url: '/doc-records', method: 'get' }),
      providesTags: ['Document'],
    }),

    // ── Framework updates ─────────────────────────────────────────────────
    getFrameworkUpdates: builder.query<FrameworkUpdateList, PageArgs | void>({
      query: (args) => ({
        url: `/updates${pageSuffix(args)}`,
        method: 'get',
      }),
      providesTags: ['Update'],
    }),

    getFrameworkUpdate: builder.query<FrameworkUpdate, string>({
      query: (updateId) => ({
        url: `/updates/${encodeURIComponent(updateId)}`,
        method: 'get',
      }),
      providesTags: ['Update'],
    }),

    /** Byte offsets and statistics only — see UpdateDiff for why there is no text. */
    getUpdateDiff: builder.query<UpdateDiff, string>({
      query: (updateId) => ({
        url: `/updates/${encodeURIComponent(updateId)}/diff`,
        method: 'get',
      }),
      providesTags: ['Update'],
    }),

    /** Each changed region with its old and new text. */
    getUpdateRegions: builder.query<UpdateRegions, string>({
      query: (updateId) => ({
        url: `/updates/${encodeURIComponent(updateId)}/regions`,
        method: 'get',
      }),
      providesTags: ['Update'],
    }),

    // ── Runs (Stage 3a) — read-only. There is deliberately no fire/prepare
    // mutation here: firing spends Azure money and waits on Stage 3c's gate.
    /**
     * Every run, or every run of ONE document.
     *
     * The engine filters by document itself, so a screen that only ever shows
     * one document's runs asks for those instead of taking all 206 and throwing
     * most of them away. Note the cache: RTK keys on the argument, so the Runs
     * tab's full list and a document's filtered list are separate entries and do
     * not share rows. That is the price of not over-fetching, and it is the
     * right way round — the filtered screens are the ones opened most often.
     */
    getRuns: builder.query<RunList, { documentId: string } | void>({
      query: (args) => ({
        url: args?.documentId
          ? `/runs?document=${encodeURIComponent(args.documentId)}`
          : '/runs',
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    // Runs put away from the Runs list. Ours, not the engine's — see
    // controllers/controlgen/run-archive.ts — so it is a tag of its own and
    // archiving never refetches the engine's run list.
    getRunArchive: builder.query<{ runIds: string[] }, void>({
      query: () => ({ url: '/run-archive', method: 'get' }),
      providesTags: ['RunArchive'],
    }),

    archiveRuns: builder.mutation<{ runIds: string[] }, { runIds: string[] }>({
      query: (body) => ({ url: '/run-archive', method: 'post', data: body }),
      invalidatesTags: ['RunArchive'],
    }),

    unarchiveRun: builder.mutation<{ runId: string }, string>({
      query: (runId) => ({
        url: `/run-archive/${encodeURIComponent(runId)}`,
        method: 'delete',
      }),
      invalidatesTags: ['RunArchive'],
    }),

    getRun: builder.query<RunDetail, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    /** One authored control. ~15KB — fetched only when a control is opened. */
    getControl: builder.query<AuthoredControl, string>({
      query: (id) => ({
        url: `/controls/${encodeURIComponent(id)}`,
        method: 'get',
        providesTags: ['Control'],
      }),
    }),

    /** Its version chain. Separate call because the control payload omits it. */
    getControlRecord: builder.query<ControlRecord, string>({
      query: (id) => ({
        url: `/controls/${encodeURIComponent(id)}/record`,
        method: 'get',
        providesTags: ['Control'],
      }),
    }),

    /** The controls a run produced. Large — 22KB on a 170-control run. */
    getRunGenerates: builder.query<RunGenerateList, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/generates`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    // ── Render packs ────────────────────────────────────────────────────────
    //
    // The engine's own projection of a run's controls onto each surface. Large
    // (~565KB for 170 controls) and deliberately not polled: fetched when a
    // surface is actually opened, and cached by run id thereafter.

    /**
     * Whether a document's original file is stored. Asked before offering the
     * original, because a 404 inside an iframe renders as a blank frame and is
     * indistinguishable from a blank document.
     */
    getDocumentOriginalInfo: builder.query<DocumentOriginalInfo, string>({
      query: (documentId) => ({
        url: `/documents/${encodeURIComponent(documentId)}/original-info`,
        method: 'get',
      }),
      providesTags: ['Document'],
    }),

    /**
     * Re-derives the control's version chain from its stored bytes. Fetched
     * alongside the record rather than on demand: a version list shown without
     * its verification is a claim presented as a fact.
     */
    getControlVerification: builder.query<ControlVerification, string>({
      query: (controlId) => ({
        url: `/controls/${encodeURIComponent(controlId)}/verify`,
        method: 'get',
        providesTags: ['Control'],
      }),
    }),

    // ── Editing ─────────────────────────────────────────────────────────────
    //
    // Reading a draft stays available even when writes are disabled: whether
    // someone left work in progress is worth knowing either way.

    getControlDraft: builder.query<ControlDraft, string>({
      query: (controlId) => ({
        url: `/controls/${encodeURIComponent(controlId)}/draft`,
        method: 'get',
        providesTags: ['Control'],
      }),
    }),

    putControlDraft: builder.mutation<
      PutDraftResult,
      {
        controlId: string;
        baseVersion: number;
        draftRevision?: number;
        fields: ControlEditFields;
      }
    >({
      query: ({ controlId, baseVersion, draftRevision, fields }) => ({
        url: `/controls/${encodeURIComponent(controlId)}/draft`,
        method: 'put',
        data: {
          base_version: baseVersion,
          ...(typeof draftRevision === 'number'
            ? { draft_revision: draftRevision }
            : {}),
          ...fields,
        },
        invalidatesTags: ['Control'],
      }),
    }),

    deleteControlDraft: builder.mutation<{ deleted: boolean }, string>({
      query: (controlId) => ({
        url: `/controls/${encodeURIComponent(controlId)}/draft`,
        method: 'delete',
        invalidatesTags: ['Control'],
      }),
    }),

    /**
     * Promote: the draft's content becomes a new immutable version.
     *
     * Invalidates `Run` so the version pane and its verification are refetched
     * — a version list that still says "1 of 1" after a promote would be a
     * screen contradicting the thing it just did.
     */
    promoteControl: builder.mutation<
      SaveControlResult,
      {
        controlId: string;
        expectedVersion: number;
        fields: ControlEditFields;
        reason?: string;
      }
    >({
      query: ({ controlId, expectedVersion, fields, reason }) => ({
        url: `/controls/${encodeURIComponent(controlId)}`,
        method: 'put',
        data: {
          expected_version: expectedVersion,
          ...fields,
          ...(reason ? { reason } : {}),
        },
      }),
      invalidatesTags: ['Run'],
    }),

    /** Where a control stands in review, and what may legally follow. */
    getControlReview: builder.query<ControlReview, string>({
      query: (controlId) => ({
        url: `/controls/${encodeURIComponent(controlId)}/review`,
        method: 'get',
      }),
      providesTags: ['Control'],
    }),

    /**
     * Move a control along the ladder. Invalidates Control so the review panel,
     * the version pane and its verification all refetch — publishing changes
     * the record's deploy pointer as well as the manifest.
     */
    transitionControlReview: builder.mutation<
      ControlReview,
      { controlId: string; state: string; reason?: string; note?: string }
    >({
      query: ({ controlId, state, reason, note }) => ({
        url: `/controls/${encodeURIComponent(controlId)}/review`,
        method: 'post',
        data: {
          new_status: { state, ...(reason ? { reason } : {}) },
          ...(note ? { note } : {}),
        },
      }),
      invalidatesTags: ['Control'],
    }),

    /** Where each of a run's controls sits in its source document. */
    /** Seven scalars per policy row, computed server-side from the run's packs. */
    getCoverageSummary: builder.query<
      CoverageSummary,
      { runId: string; documentId: string; agentId: string }
    >({
      query: ({ runId, documentId, agentId }) => ({
        url:
          `/runs/${encodeURIComponent(runId)}/coverage/summary` +
          `?documentId=${encodeURIComponent(documentId)}` +
          `&agentId=${encodeURIComponent(agentId)}`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    getRunAnchors: builder.query<RunAnchors, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/anchors`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    /**
     * The source text itself — ~92KB for the Florida statute, and the only
     * response here that is a document rather than data. Cached by document
     * id, so opening several controls of one document fetches it once.
     */
    getDocumentText: builder.query<DocumentText, string>({
      query: (documentId) => ({
        url: `/documents/${encodeURIComponent(documentId)}/text`,
        method: 'get',
      }),
      providesTags: ['Document'],
    }),

    getRunPackAssessment: builder.query<AssessmentPack, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/pack/assessment`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    getRunPackEvaluation: builder.query<EvaluationPack, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/pack/evaluation`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    /** Counts alone: the badges need one integer per control, not the whole pack. */
    // A dry run: the engine projects the run and reports what the format keeps, without building the file.
    getRunExportFidelity: builder.query<
      ExportFidelity,
      { runId: string; target: ExportTarget }
    >({
      query: ({ runId, target }) => ({
        url: `/runs/${encodeURIComponent(runId)}/export/${target}?dry_run=1`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    getRunPackEvaluationCounts: builder.query<EvaluationSeedCounts, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/pack/evaluation/counts`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    /**
     * Send this control to Asenion Redteaming.
     *
     * ONLY THE CONTROL ID TRAVELS. The server reads the description from the
     * authoring engine rather than trusting whatever the screen happens to be
     * rendering — see `controllers/controlgen/redteaming.ts`.
     *
     * The response is the redteaming service's own, so it is not typed here:
     * that shape belongs to a service this app submits to and does not own, and
     * inventing a type for it would be a claim about someone else's contract.
     *
     * NO `invalidatesTags`. Nothing this app stores changes — the work happens
     * in another service, and re-fetching the packs would say otherwise.
     */
    redteamControl: builder.mutation<Record<string, unknown>, string>({
      query: (controlId) => ({
        url: `/controls/${encodeURIComponent(controlId)}/redteam`,
        method: 'post',
      }),
    }),

    /** Send every rubric-bearing control in this run's test pack to Redteaming, as one batch. */
    redteamRun: builder.mutation<RedteamRunResult, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/redteam`,
        method: 'post',
      }),
    }),

    /** Which projects already carry this bundle's monitoring policy. Read-only. */
    getMonitoringPolicyAttachments: builder.query<
      MonitoringPolicyAttachments,
      { bundleIdentifier: string; variant: BundleVariant }
    >({
      query: ({ bundleIdentifier, variant }) => ({
        url: `/monitoring-policy/attachments?bundleIdentifier=${encodeURIComponent(bundleIdentifier)}&variant=${encodeURIComponent(variant)}`,
        method: 'GET',
      }),
      // Attaching and removing both happen OUTSIDE this API, as Apollo mutations
      // (`addProjectPolicies`, and `removeAssessment` in Governance). So the run
      // page invalidates this tag after its own attach, and refetches on mount and
      // on focus to pick up a removal made on another screen.
      providesTags: ['PolicyAttachment'],
    }),
    getRunPackRuntime: builder.query<RuntimePack, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/pack/runtime`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    /**
     * Turn this run's runtime pack into an ATTACHABLE monitoring policy.
     *
     * It LOADS, it does not ATTACH. Attaching runs through addProjectPolicies, which
     * needs `project:manage_assessments` and creates assessments — governance objects
     * an authoring action must not create as a side effect. Governance owns attach.
     *
     * REPEATABLE, NOT BLIND: pressing twice on the same run keeps the same policy `_id`
     * and an existing project attachment survives. A DIFFERENT run of the same bundle
     * identifier and version is refused with a 409 whose `data` is a
     * `MonitoringPolicyLoadRefusal`, and nothing is written; `replace: true` is the
     * explicit way through.
     */
    loadRunAsMonitoringPolicy: builder.mutation<
      MonitoringPolicyLoadResult,
      { runId: string; variant: BundleVariant; replace?: boolean }
    >({
      query: ({ runId, variant, replace }) => ({
        url: `/runs/${encodeURIComponent(runId)}/monitoring-policy`,
        method: 'post',
        // `replace` travels only as a literal true; the server refuses anything else.
        data: replace ? { variant, replace: true } : { variant },
      }),
      invalidatesTags: ['Run'],
    }),

    getRunScope: builder.query<RunScope, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/scope`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),

    getRunSpine: builder.query<RunSpine, string>({
      query: (runId) => ({
        url: `/runs/${encodeURIComponent(runId)}/spine`,
        method: 'get',
      }),
      providesTags: ['Run'],
    }),
  }),
});

export const {
  useGetEngineHealthQuery,
  useGetFrameworksPageQuery,
  useGetRegulatoryDocumentsQuery,
  useGetRegulatoryDocumentQuery,
  useGetDocRecordsQuery,
  useGetFrameworkUpdatesQuery,
  useGetFrameworkUpdateQuery,
  useGetUpdateDiffQuery,
  useGetUpdateRegionsQuery,
  useGetTrackedRegulationsQuery,
  useGetTrackedRegulationQuery,
  useCreateTrackedRegulationMutation,
  useUpdateTrackedRegulationMutation,
  useGetRegulatorySourcesQuery,
  useGetRegulatorySourceQuery,
  useCreateRegulatorySourceMutation,
  useUpdateRegulatorySourceMutation,
  useMarkRegulatorySourceCheckedMutation,
  useScanRegulatorySourceMutation,
  useGetLatestScanRunQuery,
  useGetSourceFindingQuery,
  useGetSourceFindingsQuery,
  useCreateSourceFindingMutation,
  useRejectSourceFindingMutation,
  useAcceptSourceFindingMutation,
  useReopenSourceFindingMutation,
  useGetRunsQuery,
  useGetRunArchiveQuery,
  useArchiveRunsMutation,
  useUnarchiveRunMutation,
  useGetRunQuery,
  useGetRunGeneratesQuery,
  useGetControlQuery,
  useGetControlRecordQuery,
  useGetControlVerificationQuery,
  useGetControlReviewQuery,
  useTransitionControlReviewMutation,
  useGetControlDraftQuery,
  usePutControlDraftMutation,
  useDeleteControlDraftMutation,
  usePromoteControlMutation,
  useGetRunScopeQuery,
  useLoadRunAsMonitoringPolicyMutation,
  useGetRunAnchorsQuery,
  useGetDocumentTextQuery,
  useGetDocumentOriginalInfoQuery,
  useGetRunPackAssessmentQuery,
  useGetRunPackEvaluationQuery,
  useGetRunPackEvaluationCountsQuery,
  useGetRunExportFidelityQuery,
  useGetCoverageSummaryQuery,
  useRedteamControlMutation,
  useRedteamRunMutation,
  useGetMonitoringPolicyAttachmentsQuery,
  useGetRunPackRuntimeQuery,
  useGetRunSpineQuery,
  useGetRunProgressQuery,
  useApplySpineEditsMutation,
  useResumeRunMutation,
  useStartRunMutation,
  useIngestDocumentMutation,
  useIngestDocumentFromUrlMutation,
} = controlGenApi;
